import {
  BillingCheckoutModal,
  BillingElements,
  ELEMENT_TYPES,
} from '@jusbrasil-web/billing/elements'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { BoxModalContextValue, BoxModalStatus, OneClickSuccessData } from '../../types'
import { BoxLockContext } from '../context'

type ModalProviderProps = {
  children: React.ReactNode
  resolveEmbeddedCheckout?: () => Promise<boolean>
}

export function ModalProvider(props: ModalProviderProps) {
  const { children, resolveEmbeddedCheckout } = props

  const [status, setStatus] = useState<BoxModalStatus>('closed')
  const [embeddedCheckoutEnabled, setEmbeddedCheckoutEnabled] = useState(false)
  const [prefetch, setPrefetch] = useState<boolean>(false)
  const [oneClickSuccessData, setOneClickSuccessData] = useState<OneClickSuccessData | null>(null)
  const [elementHandle, setElementHandle] = useState<BillingElements | null>(null)
  const [isCheckoutLoaded, setIsCheckoutLoaded] = useState(false)
  const prevStatusRef = useRef<BoxModalStatus | null>(null)
  const [enableSuccessScreen, setEnableSuccessScreen] = useState<boolean>(false)

  const resolveEmbeddedCheckoutRef = useRef(resolveEmbeddedCheckout)
  resolveEmbeddedCheckoutRef.current = resolveEmbeddedCheckout

  useEffect(() => {
    let cancelled = false
    if (status === 'closed') {
      setEmbeddedCheckoutEnabled(false)
      return
    } else if (status === 'open') {
      // The promise here can resolve after the modal is already closed (user opens → closes quickly → resolveEmbeddedCheckout resolves with true). React 18 makes this a no-op stale update, but it can still cause a flicker if the modal re-opens before the state settles. A cancelled flag in the effect cleanup guard this.
      resolveEmbeddedCheckoutRef.current?.().then((v) => {
        if (!cancelled) setEmbeddedCheckoutEnabled(v)
      })
    }
    return () => {
      cancelled = true
    }
  }, [status])

  const setStatusWrapper = useCallback((newStatus: BoxModalStatus) => {
    setStatus((prevStatus) => {
      prevStatusRef.current = prevStatus
      return newStatus
    })
  }, [])

  const setElementHandleWithCleanup = useCallback((handle: BillingElements | null) => {
    setElementHandle((prev) => {
      if (prev && prev.status !== 'destroyed') prev.destroy()
      return handle
    })
  }, [])

  const setOpen = useCallback(
    (open: boolean) => {
      if (open) {
        setStatusWrapper('open')
      } else {
        setStatusWrapper('closed')
        setElementHandleWithCleanup(null)
      }
    },
    [setStatusWrapper, setElementHandleWithCleanup]
  )

  const enablePrefetch = useCallback(() => {
    setPrefetch(true)
  }, [])

  const isSuccessModalOpen = useMemo(
    () => enableSuccessScreen && status === 'success' && !!oneClickSuccessData,
    [enableSuccessScreen, status, oneClickSuccessData]
  )

  const openEmbeddedCheckout = useCallback(
    (url: string, data: Record<string, string>) => {
      setIsCheckoutLoaded(false)
      const instance = new BillingElements(ELEMENT_TYPES.CheckoutModal, {
        _src: url,
        checkoutData: data,
        onLoaded: () => setIsCheckoutLoaded(true),
        onBack: () => setElementHandleWithCleanup(null),
        onClose: () => setOpen(false),
        // billing handles its own success screen inside the iframe;
        // modal closes only when user clicks "Voltar para Jusbrasil" or "X" (onClose)
      })
      setElementHandleWithCleanup(instance)
    },
    [setElementHandleWithCleanup, setOpen]
  )

  // This sets the shared status to 'closed', which closes both modals
  // and clears the success data
  const closeSuccessModal = useCallback(() => {
    setStatusWrapper('closed')
    setOneClickSuccessData(null)
  }, [setStatusWrapper])

  const isOpen = !['closed', 'success'].includes(status)

  const providerValue: BoxModalContextValue = useMemo(
    () => ({
      componentType: 'modal',
      initialized: true,
      status,
      setStatus: setStatusWrapper,
      prevStatus: prevStatusRef.current,
      open: isOpen,
      interactive: ['ready', 'error'].includes(status),
      setOpen,
      prefetch,
      enablePrefetch,
      oneClickSuccessData,
      setOneClickSuccessData,
      enableSuccessScreen,
      setEnableSuccessScreen,
      isSuccessModalOpen,
      closeSuccessModal,
      elementHandle,
      embeddedCheckoutEnabled,
      openEmbeddedCheckout,
    }),
    [
      status,
      setStatusWrapper,
      isOpen,
      setOpen,
      prefetch,
      enablePrefetch,
      oneClickSuccessData,
      enableSuccessScreen,
      isSuccessModalOpen,
      closeSuccessModal,
      elementHandle,
      embeddedCheckoutEnabled,
      openEmbeddedCheckout,
    ]
  )

  const showEmbeddedCheckout = embeddedCheckoutEnabled && elementHandle

  const boxLockChildren = showEmbeddedCheckout ? (
    <BillingCheckoutModal
      elementContext={elementHandle}
      open={isOpen}
      onOpenChange={setOpen}
      isLoaded={isCheckoutLoaded}
    ></BillingCheckoutModal>
  ) : (
    children
  )

  return <BoxLockContext.Provider value={providerValue}>{boxLockChildren}</BoxLockContext.Provider>
}
